import type {
  DiagnosticQuestion,
  DiagnosticSection,
  DiagnosticTradeOption,
} from "../types/diagnostic";

/**
 * Trade applicability — the one rule that decides what a coach is asked.
 *
 * A question tagged with a trade only applies when the business runs that
 * trade. Everything downstream (what renders, what counts toward progress,
 * what is scored, what the PDF prints) reads from here, so the fill screen and
 * the placement can never disagree about which questions were in play.
 *
 * Mirrors `App\Entity\Diagnostics\DiagnosticTrade::applies()`, which is
 * authoritative.
 */

/**
 * Trade list for the callers the backend does not serve one to.
 *
 * **Not a fallback for old payloads.** `DiagnosticSerializer::coachAttempt()`
 * emits `'availableTrades' => DiagnosticTrade::options()` unconditionally, so
 * every fill-screen payload carries the field and `CoachAttemptDetail` types it
 * non-optional. That declaration is accurate and must stay that way: loosening
 * it to `availableTrades?` would advertise a contract weaker than the one the
 * backend keeps.
 *
 * What is missing is the **company-detail** payload — `companyDetail()` serves
 * `trades` (the keys an attempt was scored against) but no option list to read
 * them against, established in work item 04. So `CompanyDetail` and the PDF it
 * builds label trade keys from this shipped list. Keys and order match
 * `DiagnosticTrade::ALL`; pinned by `diagnosticReportData.test.ts`'s "maps a
 * stored company attempt straight off the payload", which passes no
 * `tradeOptions` and still expects "HVAC".
 *
 * The cost of the list being here rather than served is drift: republish the
 * instrument with a new trade and the fill screen picks it up while the
 * history table prints the raw key. Serving `availableTrades` on the
 * company-detail payload would let this go.
 */
export const FALLBACK_TRADE_OPTIONS: DiagnosticTradeOption[] = [
  { key: "hvac", label: "HVAC" },
  { key: "electrical", label: "Electrical" },
  { key: "plumbing", label: "Plumbing" },
  { key: "roofing", label: "Roofing" },
];

export const isQuestionApplicable = (
  question: Pick<DiagnosticQuestion, "tradeTag">,
  trades: string[],
): boolean => {
  const tag = question.tradeTag;
  return tag == null || trades.includes(tag);
};

/** The questions of one section that this business is actually asked. */
export const applicableQuestions = (
  section: Pick<DiagnosticSection, "questions">,
  trades: string[],
): DiagnosticQuestion[] =>
  section.questions.filter((q) => isQuestionApplicable(q, trades));

/** Whether a diagnostic asks anything trade-specific at all. */
export const hasTradeSpecificQuestions = (
  sections: Pick<DiagnosticSection, "questions">[],
): boolean => sections.some((s) => s.questions.some((q) => q.tradeTag != null));

/** Trade keys to their labels, e.g. ["hvac"] -> "HVAC". */
export const tradeLabels = (
  trades: string[],
  options: DiagnosticTradeOption[],
): string[] =>
  trades.map((key) => options.find((o) => o.key === key)?.label ?? key);

/**
 * Add or remove a trade, keeping the backend's canonical order so the same
 * trade mix always reads and compares the same way.
 *
 * A selected key the option list does not know about is kept, at the end. The
 * add path used to rebuild the array from `options` alone, so toggling one
 * trade silently dropped any already-recorded trade the current instrument no
 * longer offers — a saved answer set quietly losing a trade it was scored
 * against.
 */
export const toggleTrade = (
  selected: string[],
  key: string,
  options: DiagnosticTradeOption[],
): string[] => {
  if (selected.includes(key)) {
    return selected.filter((t) => t !== key);
  }

  const optionKeys = options.map((o) => o.key);
  const next = [...selected, key];

  return [
    ...optionKeys.filter((k) => next.includes(k)),
    ...next.filter((k) => !optionKeys.includes(k)),
  ];
};
